// D:\powergrid\gridmind\src\crypto\verifyCertificate.js
//
// Browser-side verifier for GridMind V2 certificates.
// Mirrors certificate_registry.py EXACTLY:
//
//   body        = v2_body(row)                      // 21 fields, specific types
//   canonical   = json.dumps(body, sort_keys=True,
//                            separators=(",",":"), ensure_ascii=False)
//   record_hash = sha256(canonical).hexdigest()
//   signature   = PRIVATE_KEY.sign(record_hash.encode("ascii")).hex()
//
// Why verification failed before:
//   The signed body has 21 fields — including schema_version, issuer_key_id,
//   data_source, and finalized_at. Any verifier that omits them hashes a
//   different byte string and fails BEFORE the signature is even checked.
//   Postgres also returns numerics as strings; Python's as_number() collapses
//   integer-valued floats to ints. Both are replicated below.

const PUBLIC_KEY_PATH = "/.well-known/gridmind_ed25519_public.pem";

/* ── type coercion — mirrors the Python helpers ──────────────────────────── */

// Python as_number(): None->null; else float; integer-valued -> int.
function asNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n; // JS has one number type; canonical() prints it Python-style
}

function asInt(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

// Python bool(): matches a real boolean, but coerce defensively in case the
// transport ever hands us the string "true"/"false". Boolean("false") is TRUE
// in JS, which would silently break the hash — so handle strings explicitly.
function asBool(value) {
  if (value === true || value === false) return value;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "t" || s === "1") return true;
    if (s === "false" || s === "f" || s === "0" || s === "") return false;
  }
  if (typeof value === "number") return value !== 0;
  return Boolean(value);
}

/* ── the signed body: replicates v2_body(row) field for field ────────────── */
function v2Body(row) {
  return {
    schema_version: parseInt(row.schema_version, 10),
    issuer_key_id: row.issuer_key_id,
    assessment_id: row.assessment_id,
    certificate_id: row.certificate_id,
    feeder_id: row.feeder_id,
    region_key: row.region_key ?? null,
    intervention: row.intervention ?? null,
    intervention_date: row.intervention_date ? String(row.intervention_date) : null,
    status: row.status,
    decline_reason: row.decline_reason ?? null,
    estimated_effect_kwh_day: asNumber(row.estimated_effect_kwh_day),
    ci_lower: asNumber(row.ci_lower),
    ci_upper: asNumber(row.ci_upper),
    detectability_floor: asNumber(row.detectability_floor),
    unit: row.unit || "kWh/day",
    donors_used:
      row.donors_used !== null && row.donors_used !== undefined ? asInt(row.donors_used) : null,
    diagnostics: row.diagnostics || {},
    data_source: row.data_source,
    is_synthetic: asBool(row.is_synthetic),
    finalized_at: row.finalized_at, // MUST be the exact DB string — never pass through Date()
    previous_hash: row.previous_hash,
  };
}

/* ── canonical JSON: json.dumps(sort_keys=True, separators=(",",":"),
      ensure_ascii=False) ─────────────────────────────────────────────────── */
function pyNumber(n) {
  if (!Number.isFinite(n)) throw new Error("non-finite number in certificate");
  if (Object.is(n, -0)) return "0";
  return String(n); // String(8.4)="8.4", String(24)="24" — matches Python int/float here
}

function canonical(value) {
  if (value === null) return "null";
  const t = typeof value;
  if (t === "boolean") return value ? "true" : "false";
  if (t === "number") return pyNumber(value);
  if (t === "string") return JSON.stringify(value);
  if (Array.isArray(value)) return "[" + value.map(canonical).join(",") + "]";
  const keys = Object.keys(value).sort();
  return "{" + keys.map((k) => JSON.stringify(k) + ":" + canonical(value[k])).join(",") + "}";
}

/* ── crypto helpers ──────────────────────────────────────────────────────── */
async function sha256Hex(text) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function hexToBytes(hex) {
  const clean = String(hex).trim();
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
  return out;
}

function pemToSpki(pem) {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s+/g, "");
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

let _keyPromise = null;
async function loadPublicKey() {
  if (_keyPromise) return _keyPromise;
  _keyPromise = (async () => {
    const res = await fetch(PUBLIC_KEY_PATH);
    if (!res.ok) throw new Error(`public key not served at ${PUBLIC_KEY_PATH} (HTTP ${res.status})`);
    const pem = await res.text();
    if (!pem.includes("BEGIN PUBLIC KEY")) {
      throw new Error(`${PUBLIC_KEY_PATH} did not return a PEM (got HTML? check public/.well-known)`);
    }
    return crypto.subtle.importKey("spki", pemToSpki(pem), { name: "Ed25519" }, false, ["verify"]);
  })();
  return _keyPromise;
}

/* ── verify ──────────────────────────────────────────────────────────────── */
export async function verifyCertificateRecord(row) {
  const id = row?.certificate_id ?? "(unknown)";
  console.group(`GridMind verification: ${id}`);
  const out = {
    valid: false,
    hashOk: false,
    signatureOk: false,
    // backward-compatible aliases (UI catch-block uses these names)
    hashValid: false,
    signatureValid: false,
    reason: "",
    detail: "",
    error: null,
  };

  try {
    if (row == null) throw new Error("no record passed to verifier");

    if (row.schema_version === undefined || row.schema_version === null) {
      out.reason = "LEGACY_NOT_V2";
      out.detail = "This record predates V2 signing and is not V2-verifiable in the browser.";
      out.error = out.detail;
      console.log("legacy record — not V2-verifiable");
      return out;
    }

    const body = v2Body(row);
    const canonicalJson = canonical(body);
    const calculated = await sha256Hex(canonicalJson);
    const stored = String(row.record_hash || "").trim();

    console.log("canonical payload :", body);
    console.log("canonical JSON    :", canonicalJson);
    console.log("stored hash       :", stored);
    console.log("calculated hash   :", calculated);
    out.hashOk = calculated === stored;
    out.hashValid = out.hashOk;
    console.log("hash match        :", out.hashOk);

    if (!out.hashOk) {
      out.reason = "HASH_MISMATCH";
      out.detail =
        "Record does not hash to its stored value. Compare the 'canonical JSON' line " +
        "above against the Python v2_body for this certificate.";
      console.warn("→ FAIL at hash comparison.");
      out.error = out.detail;
      return out;
    }

    let key;
    try {
      key = await loadPublicKey();
      console.log("public key        : loaded");
    } catch (e) {
      out.reason = "PUBLIC_KEY_UNAVAILABLE";
      out.detail = e.message;
      console.warn("→ FAIL loading public key:", e.message);
      out.error = out.detail;
      return out;
    }

    const sig = hexToBytes(row.signature);
    const msg = new TextEncoder().encode(stored); // signs the hex text
    out.signatureOk = await crypto.subtle.verify({ name: "Ed25519" }, key, sig, msg);
    out.signatureValid = out.signatureOk;
    console.log("signature valid   :", out.signatureOk);

    if (!out.signatureOk) {
      out.reason = "SIGNATURE_INVALID";
      out.detail = "Hash is correct but the signature does not verify against the public key.";
      console.warn("→ FAIL at signature.");
      out.error = out.detail;
      return out;
    }

    out.valid = true;
    out.reason = "AUTHENTIC";
    out.detail = "Authentic and unaltered.";
    out.error = null;
    console.log("→ VERIFIED");
    return out;
  } catch (err) {
    out.reason = "ERROR";
    out.detail = err?.message || String(err);
    out.error = out.detail;
    console.error("→ verification threw:", err);
    return out;
  } finally {
    console.groupEnd();
  }
}

export default verifyCertificateRecord;
